import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  GraduationCap, Coins, Briefcase, MessageSquare, Bell, Video, LogOut, 
  Eye, Send, Star, Users, Camera, Clock, MapPin, User, ChevronRight,
  Wallet, FileText, X, Upload, Link as LinkIcon, Trash2, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { api, logout, setStoredUser } from '@/utils/api';
import { trackContact, trackCustomMetaEvent, trackSchedule } from '@/lib/metaPixel';

// Helper function to format date in IST
const formatDateIST = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const formatRelativeTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return formatDateIST(dateString);
};

const formatMessageDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

const extractYouTubeVideoId = (rawUrl = '') => {
  const url = rawUrl.trim();
  if (!url) return '';

  // Direct video id support
  if (/^[a-zA-Z0-9_-]{11}$/.test(url)) return url;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');

    if (host === 'youtu.be') {
      return parsed.pathname.replace('/', '').split('/')[0] || '';
    }

    if (host.endsWith('youtube.com')) {
      if (parsed.pathname === '/watch') {
        return parsed.searchParams.get('v') || '';
      }
      if (parsed.pathname.startsWith('/embed/')) {
        return parsed.pathname.split('/embed/')[1]?.split('/')[0] || '';
      }
      if (parsed.pathname.startsWith('/shorts/')) {
        return parsed.pathname.split('/shorts/')[1]?.split('/')[0] || '';
      }
    }
  } catch (e) {
    return '';
  }

  return '';
};

const getYouTubeEmbedUrl = (rawUrl = '') => {
  const videoId = extractYouTubeVideoId(rawUrl);
  if (!videoId) return '';
  const params = new URLSearchParams({
    rel: '0',
    modestbranding: '1',
    iv_load_policy: '3',
    playsinline: '1',
    loop: '1',
    playlist: videoId
  });
  return `https://www.youtube.com/embed/${videoId}?${params.toString()}`;
};

const toEpoch = (value) => {
  const ts = Date.parse(String(value || ''));
  return Number.isFinite(ts) ? ts : 0;
};

const LEAD_STATUS_OPTIONS = ['New', 'Contacted', 'Demo Scheduled', 'Converted', 'Dropped'];

const QUICK_REPLY_TEMPLATES = [
  'Hi, I can teach this subject.',
  'Sharing my available timings: 5 PM to 8 PM on weekdays.',
  'Please share your preferred start date and demo time.'
];
const REVIEW_LAST_SEEN_PREFIX = 'tutorReviewLastSeen';

const hasEssentialTutorProfile = (profile) => {
  if (!profile) return false;

  const name = String(profile.name || '').trim();
  const location = String(profile.location || '').trim();
  const mobile = String(profile.mobile || '').replace(/\D/g, '');
  const photo = String(profile.profile_photo || '').trim();
  const subjects = Array.isArray(profile.subjects) ? profile.subjects : [];
  const hasSubjectWithClass = subjects.some((item) => (
    item &&
    typeof item === 'object' &&
    String(item.subject || '').trim() &&
    Array.isArray(item.classes) &&
    item.classes.length > 0
  ));
  const hasMode = Boolean(profile.teaches_online || profile.teaches_at_home || profile.can_travel);
  const feeMin = Number(profile.fee_min || 0);
  const feeMax = Number(profile.fee_max || 0);

  return Boolean(
    name &&
    location &&
    mobile.length === 10 &&
    photo &&
    hasSubjectWithClass &&
    hasMode &&
    feeMin > 0 &&
    feeMax > feeMin
  );
};

export default function TutorDashboard({ user, setUser }) {
  const [profile, setProfile] = useState(null);
  const [introVideoInput, setIntroVideoInput] = useState('');
  const [isEditingVideo, setIsEditingVideo] = useState(false);
  const [stats, setStats] = useState({ profile_views: 0, applications: 0, rating: 0, coins: 0 });
  const [messages, setMessages] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [requirements, setRequirements] = useState([]);
  const [viewedRequirementIds, setViewedRequirementIds] = useState({});
  const [photoUrl, setPhotoUrl] = useState('');
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [photoInputUrl, setPhotoInputUrl] = useState('');
  const [unlockingRequirementId, setUnlockingRequirementId] = useState(null);
  const validTabs = ['overview', 'jobs', 'messages', 'profile', 'reviews'];
  const getInitialTab = () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlTab = params.get('tab');
      if (urlTab && validTabs.includes(urlTab)) return urlTab;
      if (sessionStorage.getItem('forceJobsTab') === '1') {
        sessionStorage.removeItem('forceJobsTab');
        return 'jobs';
      }
    } catch (e) {
      // ignore
    }
    return (
      localStorage.getItem('tutorDashboardTab') ||
      sessionStorage.getItem('tutorDashboardTab') ||
      'overview'
    );
  };
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [unlockCache, setUnlockCache] = useState(() => {
    try {
      const saved = localStorage.getItem('tutorUnlockCache');
      return saved ? JSON.parse(saved) : {};
    } catch (e) {
      return {};
    }
  });
  const [reviews, setReviews] = useState([]);
  const [reviewUnreadCount, setReviewUnreadCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
  const [showNotificationsModal, setShowNotificationsModal] = useState(false);
  const [notificationFilter, setNotificationFilter] = useState('all');
  const [highlightedRequirementId, setHighlightedRequirementId] = useState(null);
  const [leadStatusUpdatingId, setLeadStatusUpdatingId] = useState(null);
  const [leadStatusEditId, setLeadStatusEditId] = useState(null);
  const [leadStatusDraft, setLeadStatusDraft] = useState('');
  const [jobFilters, setJobFilters] = useState({
    subject: '',
    mode: '',
    language: '',
    gender_preference: '',
    time_preference: '',
    board: '',
    min_budget: '',
    max_budget: '',
    sort_by: 'recent'
  });
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const showEssentialProfileBanner = Boolean(profile) && !hasEssentialTutorProfile(profile);

  useEffect(() => {
    loadProfile();
    loadStats();
    loadMessages();
    loadConversations();
    loadRequirements();
    loadReviews();
    loadNotifications();
  }, []);

  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const currentUrlTab = params.get('tab');
      if (activeTab && currentUrlTab !== activeTab) {
        params.set('tab', activeTab);
        const nextUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', nextUrl);
      }
    } catch (e) {
      // ignore
    }
  }, [activeTab]);

  useEffect(() => {
    const onPopState = () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tabFromUrl = params.get('tab');
        if (tabFromUrl && validTabs.includes(tabFromUrl)) {
          setActiveTab(tabFromUrl);
          sessionStorage.setItem('tutorDashboardTab', tabFromUrl);
          localStorage.setItem('tutorDashboardTab', tabFromUrl);
        }
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  const persistActiveTab = (tab, pushHistory = true) => {
    if (!validTabs.includes(tab)) return;
    setActiveTab(tab);
    sessionStorage.setItem('tutorDashboardTab', tab);
    localStorage.setItem('tutorDashboardTab', tab);
    try {
      const params = new URLSearchParams(window.location.search);
      params.set('tab', tab);
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      if (pushHistory) {
        window.history.pushState({}, '', newUrl);
      } else {
        window.history.replaceState({}, '', newUrl);
      }
    } catch (e) {
      // ignore
    }
  };
  
  const handleTabChange = (tab) => {
    persistActiveTab(tab, true);
  };

  const getReviewLastSeenKey = () => `${REVIEW_LAST_SEEN_PREFIX}:${user?.id || 'unknown'}`;

  const getReviewTimestamp = (review) => {
    const created = Date.parse(review?.created_at || '');
    const updated = Date.parse(review?.updated_at || '');
    return Math.max(Number.isFinite(created) ? created : 0, Number.isFinite(updated) ? updated : 0);
  };

  const getReviewLastSeenTs = () => {
    try {
      const raw = localStorage.getItem(getReviewLastSeenKey());
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : 0;
    } catch (_err) {
      return 0;
    }
  };

  const updateReviewUnreadCount = (reviewList) => {
    const lastSeenTs = getReviewLastSeenTs();
    const unread = (reviewList || []).filter((review) => getReviewTimestamp(review) > lastSeenTs).length;
    setReviewUnreadCount(unread);
  };

  const markReviewsSeen = (reviewList) => {
    const maxReviewTs = Math.max(0, ...(reviewList || []).map(getReviewTimestamp));
    const seenTs = Math.max(Date.now(), maxReviewTs);
    try {
      localStorage.setItem(getReviewLastSeenKey(), String(seenTs));
    } catch (_err) {
      // ignore storage errors
    }
    setReviewUnreadCount(0);
  };

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [conversationMessages]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadStats();
      loadConversations();
      loadNotifications();
      if (selectedConversation?.id) {
        loadConversationThread(selectedConversation.id, selectedConversation.name);
      }
    }, 30000);
    return () => clearInterval(intervalId);
  }, [selectedConversation]);

  const loadProfile = async () => {
    try {
      const response = await api.get('/tutor/profile');
      setProfile(response.data);
      const savedVideoUrl = response.data?.intro_video_url || '';
      setIntroVideoInput(savedVideoUrl);
      setIsEditingVideo(!savedVideoUrl);
      setPhotoUrl(response.data.profile_photo || '');
    } catch (error) {
      console.error('Failed to load profile:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.get('/tutor/stats');
      setStats(response.data);
      if (setUser && user) {
        const updatedUser = { ...user, coins: response.data.coins };
        setUser(updatedUser);
        setStoredUser(updatedUser);
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadMessages = async () => {
    try {
      const [messagesRes, unreadRes] = await Promise.all([
        api.get('/messages'),
        api.get('/messages/unread')
      ]);
      setMessages(messagesRes.data);
      setUnreadCount(unreadRes.data.count);
    } catch (error) {
      console.error('Failed to load messages:', error);
    }
  };

  const loadConversations = async () => {
    try {
      const response = await api.get('/messages/conversations');
      const list = Array.isArray(response.data) ? response.data : [];
      setConversations(list);
      const totalUnread = list.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadRequirements = async () => {
    try {
      const params = new URLSearchParams();
      Object.entries(jobFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          params.append(key, String(value).trim());
        }
      });
      const query = params.toString();
      const response = await api.get(`/requirements${query ? `?${query}` : ''}`);
      const merged = response.data.map((req) => {
        const cached = unlockCache[req.id];
        if (cached) {
          return {
            ...req,
            contact_unlocked: true,
            phone: cached.phone || req.phone,
            unlocked_at: cached.unlocked_at || req.unlocked_at
          };
        }
        return req;
      });
      setRequirements(merged);
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  const resetJobFilters = async () => {
    const next = {
      subject: '',
      mode: '',
      language: '',
      gender_preference: '',
      time_preference: '',
      board: '',
      min_budget: '',
      max_budget: '',
      sort_by: 'recent'
    };
    setJobFilters(next);
    try {
      const response = await api.get('/requirements?sort_by=recent');
      const merged = response.data.map((req) => {
        const cached = unlockCache[req.id];
        if (cached) {
          return {
            ...req,
            contact_unlocked: true,
            phone: cached.phone || req.phone,
            unlocked_at: cached.unlocked_at || req.unlocked_at
          };
        }
        return req;
      });
      setRequirements(merged);
    } catch (error) {
      console.error('Failed to reset requirements:', error);
    }
  };

  const loadNotifications = async () => {
    try {
      const [listRes, unreadRes] = await Promise.all([
        api.get('/notifications?limit=40'),
        api.get('/notifications/unread')
      ]);
      setNotifications(Array.isArray(listRes.data) ? listRes.data : []);
      setNotificationsUnreadCount(unreadRes.data?.count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    }
  };

  const openNotifications = async () => {
    setShowNotificationsModal(true);
    await loadNotifications();
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setNotificationsUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification) return;
    const meta = notification.meta || {};

    try {
      await api.put(`/notifications/${notification.id}/read`);
      setNotifications((prev) => prev.map((n) => (
        n.id === notification.id ? { ...n, read: true } : n
      )));
      setNotificationsUnreadCount((prev) => Math.max(0, prev - (notification.read ? 0 : 1)));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }

    setShowNotificationsModal(false);

    if (notification.type === 'message_received' && meta.sender_id) {
      persistActiveTab('messages', true);
      await loadConversationThread(meta.sender_id, meta.sender_name || 'Student');
      return;
    }

    if (notification.type === 'new_requirement_match' && meta.requirement_id) {
      persistActiveTab('jobs', true);
      setHighlightedRequirementId(meta.requirement_id);
      setTimeout(() => {
        const el = document.getElementById(`requirement-${meta.requirement_id}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 120);
      setTimeout(() => setHighlightedRequirementId(null), 2500);
      return;
    }

    persistActiveTab('overview', true);
  };

  const markNotificationUnread = async (notificationId) => {
    try {
      await api.put(`/notifications/${notificationId}/unread`);
      setNotifications((prev) => prev.map((n) => (
        n.id === notificationId ? { ...n, read: false } : n
      )));
      setNotificationsUnreadCount((prev) => prev + 1);
    } catch (error) {
      toast.error('Failed to mark notification as unread');
    }
  };

  const loadReviews = async () => {
    try {
      const response = await api.get('/reviews/my/received');
      const reviewList = Array.isArray(response.data) ? response.data : [];
      setReviews(reviewList);
      updateReviewUnreadCount(reviewList);
    } catch (error) {
      console.error('Failed to load reviews:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'reviews') {
      markReviewsSeen(reviews);
    }
  }, [activeTab, reviews]);

  const handleDeleteProfile = async () => {
    try {
      await api.delete('/profile/delete');
      toast.success('Profile deleted successfully');
      logout();
      setUser(null);
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete profile');
    }
  };

  const unlockedRequirements = requirements
    .filter((req) => !!req.contact_unlocked)
    .sort((a, b) => {
      const unlockDiff = toEpoch(b.unlocked_at) - toEpoch(a.unlocked_at);
      if (unlockDiff !== 0) return unlockDiff;
      return toEpoch(b.created_at) - toEpoch(a.created_at);
    });
  const filteredNotifications = notifications.filter(
    (n) => notificationFilter === 'all' || n.type === notificationFilter
  );
  const unreadInFilter = filteredNotifications.filter((n) => !n.read).length;

  const profileSubjects = (profile?.subjects || [])
    .map((s) => (s?.subject || '').toLowerCase().trim())
    .filter(Boolean);

  const profileClasses = (profile?.subjects || [])
    .flatMap((s) => (Array.isArray(s?.classes) ? s.classes : []))
    .map((c) => String(c).toLowerCase().trim());

  const getRequirementMatch = (req) => {
    const reasons = [];
    let score = Number(req?.relevance_score || 0);

    const reqSubject = String(req?.subject || '').toLowerCase().trim();
    if (reqSubject && profileSubjects.includes(reqSubject)) {
      reasons.push('Exact subject match');
    }

    const reqClass = String(req?.level_class || '').toLowerCase().trim();
    if (reqClass && profileClasses.includes(reqClass)) {
      reasons.push('Class match');
    }

    const reqLocation = String(req?.location || '').toLowerCase().trim();
    const tutorLocation = String(profile?.location || '').toLowerCase().trim();
    if (reqLocation && tutorLocation && (reqLocation.includes(tutorLocation) || tutorLocation.includes(reqLocation))) {
      reasons.push('Location match');
    } else if (reqLocation && tutorLocation) {
      const reqCity = reqLocation.split('-')[0]?.trim();
      const tutorCity = tutorLocation.split('-')[0]?.trim();
      if (reqCity && tutorCity && reqCity === tutorCity) reasons.push('Same city');
    }

    const reqModes = Array.isArray(req?.mode) ? req.mode : [req?.mode];
    const reqModesNorm = reqModes.map((m) => String(m || '').toLowerCase());
    const modeMatched = (
      (profile?.teaches_online && reqModesNorm.some((m) => m.includes('online'))) ||
      (profile?.teaches_at_home && reqModesNorm.some((m) => m.includes('home') || m.includes('tutor'))) ||
      (profile?.can_travel && reqModesNorm.some((m) => m.includes('student') || m.includes('offline') || m.includes('home')))
    );
    if (modeMatched) {
      reasons.push('Teaching mode match');
    }

    try {
      const createdAt = new Date(req?.created_at);
      const ageHours = (Date.now() - createdAt.getTime()) / 3600000;
      if (ageHours <= 24) reasons.push('Recent activity');
    } catch (_err) {
      // ignore
    }

    return { score, reasons };
  };

  const bestMatches = requirements
    .filter((req) => !req.contact_unlocked)
    .map((req) => ({ req, match: getRequirementMatch(req) }))
    .filter((item) => item.match.score > 0)
    .sort((a, b) => b.match.score - a.match.score)
    .slice(0, 5);

  const loadConversationThread = async (partnerId, partnerName) => {
    try {
      const response = await api.get(`/messages/thread/${partnerId}`);
      setConversationMessages(response.data.messages);
      setSelectedConversation({ id: partnerId, name: partnerName });
      await loadConversations(); // Refresh unread counts after backend marks read
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConversation) return;
    
    try {
      await api.post('/messages', {
        recipient_id: selectedConversation.id,
        message: newMessage
      });
      setNewMessage('');
      loadConversationThread(selectedConversation.id, selectedConversation.name);
      toast.success('Message sent!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const applyQuickReply = (template) => {
    setNewMessage((prev) => {
      const current = String(prev || '').trim();
      return current ? `${current} ${template}` : template;
    });
  };

  const updateProfilePhoto = async () => {
    if (!photoUrl.trim()) {
      toast.error('Please enter a photo URL');
      return;
    }
    
    try {
      await api.post(`/tutor/profile/photo?photo_url=${encodeURIComponent(photoUrl)}`);
      toast.success('Profile photo updated!');
      loadProfile();
    } catch (error) {
      toast.error('Failed to update photo');
    }
  };

  const handleViewRequirement = async (reqId) => {
    persistActiveTab('jobs', false);
    const existing = requirements.find(r => r.id === reqId);
    if (existing?.contact_unlocked) {
      toast.info('Contact already unlocked.');
      return;
    }

    try {
      setUnlockingRequirementId(reqId);
      const response = await api.post('/wallet/spend', null, {
        params: { coins: 200, purpose: 'view_requirement', target_id: reqId }
      });
      
      const unlockedPhone = response.data?.data?.phone || '';
      const unlockedAt = response.data?.unlocked_at || new Date().toISOString();
      
      // Update user coins
      const updatedUser = { ...user, coins: response.data.remaining_coins };
      setUser(updatedUser);
      setStoredUser(updatedUser);
      
      // Update unlock cache
      setUnlockCache(prev => {
        const next = { ...prev, [reqId]: { phone: unlockedPhone, unlocked_at: unlockedAt } };
        localStorage.setItem('tutorUnlockCache', JSON.stringify(next));
        return next;
      });
      
      // Update the requirements state directly without page reload
      setRequirements(prev => prev.map(req => {
        if (req.id !== reqId) return req;
        return {
          ...req,
          contact_unlocked: true,
          phone: unlockedPhone,
          unlocked_at: unlockedAt,
          lead_status: 'New'
        };
      }));
      
      // Refresh stats to update coin display
      loadStats();
      trackContact({
        content_name: 'tutor_unlock_requirement',
        content_category: 'Student Requirement Unlock',
        content_ids: [reqId],
        value: response.data.used_free_requirement_unlock ? 0 : 200,
        currency: 'INR',
      }, {
        external_id: user?.id,
        email: user?.email,
        mobile: user?.mobile,
      });
      
      // Show success message only; phone is visible inline in unlocked state
      toast.success(response.data.used_free_requirement_unlock ? 'Free lead unlocked!' : 'Contact unlocked!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlock requirement');
    } finally {
      setUnlockingRequirementId(null);
      persistActiveTab('jobs', false);
    }
  };

  const updateLeadStatus = async (requirementId, nextStatus) => {
    if (!LEAD_STATUS_OPTIONS.includes(nextStatus)) return;
    try {
      setLeadStatusUpdatingId(requirementId);
      await api.put(`/tutor/leads/${requirementId}/status`, null, {
        params: { status: nextStatus }
      });
      setRequirements((prev) => prev.map((req) => (
        req.id === requirementId ? { ...req, lead_status: nextStatus } : req
      )));
      if (nextStatus === 'Demo Scheduled') {
        trackSchedule({
          content_name: 'tutor_lead_demo_scheduled',
          content_category: 'Tutor Lead Status',
          content_ids: [requirementId],
          status: nextStatus,
        }, {
          external_id: user?.id,
          email: user?.email,
          mobile: user?.mobile,
        });
      } else {
        trackCustomMetaEvent('TutorLeadStatusUpdated', {
          content_name: 'tutor_lead_status_updated',
          content_category: 'Tutor Lead Status',
          content_ids: [requirementId],
          status: nextStatus,
        }, {
          external_id: user?.id,
          email: user?.email,
          mobile: user?.mobile,
        });
      }
      toast.success(`Lead moved to ${nextStatus}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update lead status');
    } finally {
      setLeadStatusUpdatingId(null);
    }
  };

  const beginLeadStatusEdit = (req) => {
    setLeadStatusEditId(req.id);
    setLeadStatusDraft(req.lead_status || 'New');
  };

  const cancelLeadStatusEdit = () => {
    setLeadStatusEditId(null);
    setLeadStatusDraft('');
  };

  const saveLeadStatusEdit = async (req) => {
    const nextStatus = leadStatusDraft || req.lead_status || 'New';
    await updateLeadStatus(req.id, nextStatus);
    setLeadStatusEditId(null);
    setLeadStatusDraft('');
  };

  if (!profile) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50 shadow-sm">
        <nav className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <div>
              <span className="text-xl font-bold text-gray-900">Tricity Tutors</span>
              <span className="text-xs text-gray-500 block">Chandigarh Region</span>
            </div>
          </Link>
          
          {/* Navigation Tabs */}
          <div className="hidden md:flex items-center gap-1 bg-gray-100 rounded-full p-1">
            <button
              onClick={() => handleTabChange('overview')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'overview' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Dashboard
            </button>
            <button
              onClick={() => handleTabChange('jobs')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'jobs' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Find Students
            </button>
            <button
              onClick={() => handleTabChange('messages')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all relative ${
                activeTab === 'messages' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Messages
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
            <button
              onClick={() => handleTabChange('profile')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                activeTab === 'profile' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Profile
            </button>
            <button
              onClick={() => handleTabChange('reviews')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all relative ${
                activeTab === 'reviews' ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Reviews
              {reviewUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs min-w-5 h-5 px-1 rounded-full flex items-center justify-center">
                  {reviewUnreadCount}
                </span>
              )}
            </button>
          </div>
          
          <div className="flex items-center gap-3">
            <Link to="/wallet">
              <Button variant="outline" className="rounded-full flex items-center gap-2 border-yellow-300 bg-yellow-50">
                <Coins className="w-4 h-4 text-yellow-600" />
                <span className="font-semibold text-yellow-700">{stats?.coins ?? user?.coins ?? 0}</span>
              </Button>
            </Link>
            <Button variant="outline" className="rounded-full relative" onClick={openNotifications}>
              <Bell className="w-4 h-4" />
              {notificationsUnreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  {notificationsUnreadCount}
                </span>
              )}
            </Button>
            <Button onClick={logout} variant="outline" className="rounded-full">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Welcome Section */}
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center">
                <GraduationCap className="w-7 h-7 text-indigo-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Welcome back, {profile.name?.split(' ')[0]}!</h1>
                <p className="text-gray-500 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Last login: {formatDateIST(profile.last_login || new Date().toISOString())}
                </p>
              </div>
            </div>

            {showEssentialProfileBanner && (
              <Card className="border-amber-200 bg-amber-50 shadow-sm">
                <CardContent className="p-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-amber-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-amber-900">Complete your essential profile</p>
                      <p className="text-sm text-amber-800">
                        Please complete your essential profile to get featured on Browse Tutors Page and student search results.
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    className="rounded-full bg-amber-600 hover:bg-amber-700 text-white"
                    onClick={() => navigate('/tutor/profile/edit')}
                  >
                    Complete Profile
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Eye className="w-4 h-4" />
                    <span className="text-xs">Profile Views</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.profile_views || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Send className="w-4 h-4" />
                    <span className="text-xs">Applications</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.applications || 0}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Star className="w-4 h-4" />
                    <span className="text-xs">Rating</span>
                  </div>
                  <p className="text-2xl font-bold text-gray-900">{stats.rating?.toFixed(1) || '0.0'}</p>
                </CardContent>
              </Card>
              
              <Card className="bg-white border-0 shadow-sm">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-gray-500 mb-1">
                    <Coins className="w-4 h-4" />
                    <span className="text-xs">Coins</span>
                  </div>
                  <p className="text-2xl font-bold text-yellow-600">{stats.coins || user?.coins || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Tab Content */}
            {activeTab === 'overview' && (
              <div className="space-y-6">
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">Best Matches For You</CardTitle>
                      <Button variant="ghost" size="sm" onClick={() => handleTabChange('jobs')}>
                        View All <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {bestMatches.length === 0 ? (
                      <p className="text-gray-500 text-sm">No strong matches yet. We will notify you when matching jobs arrive.</p>
                    ) : (
                      <div className="space-y-3">
                        {bestMatches.map(({ req, match }) => (
                          <div key={req.id} className="p-3 border rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold text-gray-900">{req.subject} • {req.level_class}</p>
                                <p className="text-xs text-gray-500">{req.location}</p>
                                <p className="text-xs text-gray-500">
                                  {req.board ? `${req.board} • ` : ''}{(req.budget_min || req.budget_max) ? `Budget ₹${req.budget_min || 0}-${req.budget_max || req.budget_min || 0}/hr` : 'Budget not specified'}
                                </p>
                              </div>
                              <Badge className="bg-indigo-100 text-indigo-700">Match {match.score}%</Badge>
                            </div>
                            <p className="text-xs text-gray-600 mt-2">Why this match: {match.reasons.join(', ')}</p>
                            <div className="mt-2">
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleViewRequirement(req.id)}
                                disabled={unlockingRequirementId === req.id}
                                className="rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70"
                              >
                                {unlockingRequirementId === req.id ? 'Unlocking...' : 'Unlock Contact (200 coins)'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Unlocked Queries */}
                <Card className="bg-white border-0 shadow-sm">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <Briefcase className="w-5 h-5 text-green-600" />
                        </div>
                        <CardTitle className="text-lg">Unlocked Queries</CardTitle>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleTabChange('jobs')}>
                        Find Students <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-gray-600 mb-3">
                      Select the appropriate lead status for each unlocked query. Use <span className="font-semibold">Edit Status</span> to update it when progress changes.
                    </p>
                    {unlockedRequirements.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">No unlocked queries yet</p>
                    ) : (
                      <div className="space-y-3">
                        {unlockedRequirements.map((req) => (
                          <div key={req.id} className="p-4 border rounded-lg hover:border-indigo-300 transition-colors">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <h3 className="font-semibold text-lg text-gray-900">{req.subject}</h3>
                                <p className="text-sm text-gray-500">{req.level_class} • {req.mode}</p>
                                <p className="text-xs text-gray-500 mt-1">
                                  {req.board ? `${req.board} • ` : ''}{(req.budget_min || req.budget_max) ? `Budget ₹${req.budget_min || 0}-${req.budget_max || req.budget_min || 0}/hr` : 'Budget not specified'}
                                </p>
                              </div>
                              <Badge>{req.time_preference}</Badge>
                            </div>
                            <p className="text-sm text-gray-600 mb-3">{req.description || 'No description provided'}</p>
                            <div className="flex justify-between items-center mt-3">
                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {req.location}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {formatDateIST(req.created_at)}
                                </span>
                              </div>
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-xs text-green-600 font-medium">✓ Unlocked</span>
                                {leadStatusEditId === req.id ? (
                                  <div className="mt-1 flex items-center gap-1">
                                    <select
                                      value={leadStatusDraft}
                                      onChange={(e) => setLeadStatusDraft(e.target.value)}
                                      disabled={leadStatusUpdatingId === req.id}
                                      className="h-8 rounded-md border px-2 text-xs bg-white"
                                    >
                                      {LEAD_STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>{status}</option>
                                      ))}
                                    </select>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 px-2 text-xs"
                                      disabled={leadStatusUpdatingId === req.id}
                                      onClick={() => saveLeadStatusEdit(req)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-xs"
                                      disabled={leadStatusUpdatingId === req.id}
                                      onClick={cancelLeadStatusEdit}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="mt-1 flex items-center gap-2">
                                    <Badge variant="outline" className="text-xs">{req.lead_status || 'New'}</Badge>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-xs"
                                      onClick={() => beginLeadStatusEdit(req)}
                                    >
                                      Edit Status
                                    </Button>
                                  </div>
                                )}
                                {req.phone && (
                                  <span className="text-xs font-semibold text-orange-600">
                                    📞 {req.phone}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'jobs' && (
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Briefcase className="w-5 h-5" />
                    All Student Requirements
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                    <Input
                      value={jobFilters.subject}
                      onChange={(e) => setJobFilters((p) => ({ ...p, subject: e.target.value }))}
                      placeholder="Subject"
                    />
                    <Input
                      value={jobFilters.language}
                      onChange={(e) => setJobFilters((p) => ({ ...p, language: e.target.value }))}
                      placeholder="Language"
                    />
                    <select
                      value={jobFilters.mode}
                      onChange={(e) => setJobFilters((p) => ({ ...p, mode: e.target.value }))}
                      className="h-10 rounded-md border px-3 bg-white text-sm"
                    >
                      <option value="">Mode (Any)</option>
                      <option value="Online">Online</option>
                      <option value="Home">Home Tuition</option>
                      <option value="I can travel">I can travel</option>
                    </select>
                    <select
                      value={jobFilters.gender_preference}
                      onChange={(e) => setJobFilters((p) => ({ ...p, gender_preference: e.target.value }))}
                      className="h-10 rounded-md border px-3 bg-white text-sm"
                    >
                      <option value="">Gender (Any)</option>
                      <option value="Any">Any</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                    </select>
                    <select
                      value={jobFilters.time_preference}
                      onChange={(e) => setJobFilters((p) => ({ ...p, time_preference: e.target.value }))}
                      className="h-10 rounded-md border px-3 bg-white text-sm"
                    >
                      <option value="">Timing (Any)</option>
                      <option value="Part Time">Part Time</option>
                      <option value="Full Time">Full Time</option>
                    </select>
                    <select
                      value={jobFilters.board}
                      onChange={(e) => setJobFilters((p) => ({ ...p, board: e.target.value }))}
                      className="h-10 rounded-md border px-3 bg-white text-sm"
                    >
                      <option value="">Board (Any)</option>
                      <option value="CBSE">CBSE</option>
                      <option value="ICSE">ICSE</option>
                      <option value="State Board">State Board</option>
                      <option value="IB">IB</option>
                      <option value="IGCSE">IGCSE</option>
                    </select>
                    <Input
                      type="number"
                      min="0"
                      value={jobFilters.min_budget}
                      onChange={(e) => setJobFilters((p) => ({ ...p, min_budget: e.target.value }))}
                      placeholder="Min Budget"
                    />
                    <Input
                      type="number"
                      min="0"
                      value={jobFilters.max_budget}
                      onChange={(e) => setJobFilters((p) => ({ ...p, max_budget: e.target.value }))}
                      placeholder="Max Budget"
                    />
                    <select
                      value={jobFilters.sort_by}
                      onChange={(e) => setJobFilters((p) => ({ ...p, sort_by: e.target.value }))}
                      className="h-10 rounded-md border px-3 bg-white text-sm"
                    >
                      <option value="recent">Sort: Recent</option>
                      <option value="relevance">Sort: Relevance</option>
                    </select>
                  </div>
                  <div className="flex gap-2 mb-5">
                    <Button type="button" size="sm" onClick={loadRequirements} className="bg-indigo-600 hover:bg-indigo-700">
                      Apply Filters
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={resetJobFilters}>
                      Reset
                    </Button>
                  </div>
                  <p className="text-xs text-gray-600 mb-3">
                    Teachers should select the appropriate lead status for unlocked requirements. Click <span className="font-semibold">Edit Status</span> to change it.
                  </p>
                  <div className="space-y-4">
                    {requirements.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No job requirements available</p>
                    ) : (
                      requirements.map((req) => {
                        const isUnlocked = !!req.contact_unlocked;
                        return (
                        <div
                          id={`requirement-${req.id}`}
                          key={req.id}
                          className={`p-4 border rounded-lg hover:border-indigo-300 transition-colors ${
                            highlightedRequirementId === req.id ? 'ring-2 ring-indigo-400 bg-indigo-50/40' : ''
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <h3 className="font-semibold text-lg">{req.subject}</h3>
                              <p className="text-sm text-gray-500">{req.level_class} • {req.mode}</p>
                              <p className="text-xs text-gray-500 mt-1">
                                {req.board ? `${req.board} • ` : ''}{(req.budget_min || req.budget_max) ? `Budget ₹${req.budget_min || 0}-${req.budget_max || req.budget_min || 0}/hr` : 'Budget not specified'}
                              </p>
                            </div>
                            <Badge>{req.time_preference}</Badge>
                          </div>
                          <p className="text-sm text-gray-600 mb-3">
                            {isUnlocked ? (req.description || 'No description provided') : 'Description hidden. Unlock contact to view details.'}
                          </p>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {req.location}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {formatDateIST(req.created_at)}
                              </span>
                            </div>
                            {isUnlocked ? (
                              <div className="flex flex-col items-end gap-1">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="rounded-full border-green-500 bg-green-50 text-green-700 cursor-default"
                                  disabled
                                >
                                  ✓ Contact Unlocked
                                </Button>
                                {leadStatusEditId === req.id ? (
                                  <div className="flex items-center gap-1">
                                    <select
                                      value={leadStatusDraft}
                                      onChange={(e) => setLeadStatusDraft(e.target.value)}
                                      disabled={leadStatusUpdatingId === req.id}
                                      className="h-8 rounded-md border px-2 text-xs bg-white"
                                    >
                                      {LEAD_STATUS_OPTIONS.map((status) => (
                                        <option key={status} value={status}>{status}</option>
                                      ))}
                                    </select>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      className="h-8 px-2 text-xs"
                                      disabled={leadStatusUpdatingId === req.id}
                                      onClick={() => saveLeadStatusEdit(req)}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-xs"
                                      disabled={leadStatusUpdatingId === req.id}
                                      onClick={cancelLeadStatusEdit}
                                    >
                                      Cancel
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-500">Lead:</span>
                                    <Badge variant="outline" className="text-xs">{req.lead_status || 'New'}</Badge>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      className="h-8 px-2 text-xs"
                                      onClick={() => beginLeadStatusEdit(req)}
                                    >
                                      Edit Status
                                    </Button>
                                  </div>
                                )}
                                {req.phone && (
                                  <span className="text-sm font-semibold text-orange-600 flex items-center gap-1">
                                    📞 {req.phone}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleViewRequirement(req.id)}
                                disabled={unlockingRequirementId === req.id}
                                className="rounded-full bg-orange-500 hover:bg-orange-600 disabled:opacity-70"
                              >
                                {unlockingRequirementId === req.id ? 'Unlocking...' : 'Unlock Contact (200 coins)'}
                              </Button>
                            )}
                          </div>
                        </div>
                      )})
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'messages' && (
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader className="border-b">
                  <CardTitle className="flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Conversations
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="grid grid-cols-1 md:grid-cols-3 min-h-[500px]">
                    {/* Conversation List */}
                    <div className="border-r">
                      {conversations.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <MessageSquare className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                          <p>No conversations yet</p>
                          <p className="text-xs mt-1">Messages from students will appear here</p>
                        </div>
                      ) : (
                        conversations.map((conv) => (
                          <div
                            key={conv.partner_id}
                            onClick={() => loadConversationThread(conv.partner_id, conv.partner_name)}
                            className={`p-4 border-b cursor-pointer hover:bg-gray-50 transition-colors ${
                              selectedConversation?.id === conv.partner_id ? 'bg-indigo-50 border-l-4 border-l-indigo-600' : ''
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Avatar className="w-10 h-10">
                                <AvatarFallback className="bg-indigo-100 text-indigo-600">
                                  {conv.partner_name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <p className="font-medium text-gray-900 truncate">{conv.partner_name}</p>
                                  {conv.unread_count > 0 && (
                                    <Badge className="bg-red-500 text-xs">{conv.unread_count}</Badge>
                                  )}
                                </div>
                                <p className="text-sm text-gray-500 truncate">{conv.last_message}</p>
                                <p className="text-xs text-gray-400">{formatMessageDateTime(conv.last_message_time)}</p>
                              </div>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    
                    {/* Message Thread */}
                    <div className="md:col-span-2 flex flex-col">
                      {selectedConversation ? (
                        <>
                          <div className="p-4 border-b bg-gray-50">
                            <div className="flex items-center gap-3">
                              <Avatar>
                                <AvatarFallback className="bg-indigo-100 text-indigo-600">
                                  {selectedConversation.name?.charAt(0) || 'U'}
                                </AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{selectedConversation.name}</p>
                                <p className="text-xs text-gray-500">Student</p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[350px]">
                            {conversationMessages.map((msg, idx) => (
                              <div
                                key={idx}
                                className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                              >
                                <div className={`max-w-[70%] p-3 rounded-lg ${
                                  msg.sender_id === user.id 
                                    ? 'bg-indigo-600 text-white' 
                                    : 'bg-gray-100 text-gray-900'
                                }`}>
                                  <p className="text-sm">{msg.message}</p>
                                  <p className={`text-xs mt-1 ${
                                    msg.sender_id === user.id ? 'text-indigo-200' : 'text-gray-400'
                                  }`}>
                                    {formatMessageDateTime(msg.created_at)}
                                  </p>
                                </div>
                              </div>
                            ))}
                            <div ref={messagesEndRef} />
                          </div>
                          
                          <div className="p-4 border-t bg-gray-50">
                            {user?.role === 'tutor' && (
                              <div className="flex flex-wrap gap-2 mb-3">
                                {QUICK_REPLY_TEMPLATES.map((template) => (
                                  <Button
                                    key={template}
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="rounded-full text-xs"
                                    onClick={() => applyQuickReply(template)}
                                  >
                                    {template}
                                  </Button>
                                ))}
                              </div>
                            )}
                            <div className="flex gap-2">
                              <Input
                                value={newMessage}
                                onChange={(e) => setNewMessage(e.target.value)}
                                placeholder="Type your message..."
                                className="flex-1"
                                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                              />
                              <Button onClick={sendMessage} className="bg-indigo-600 hover:bg-indigo-700">
                                <Send className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="flex-1 flex items-center justify-center text-gray-500">
                          <div className="text-center">
                            <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                            <p>Select a conversation to view messages</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'profile' && (
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Edit Profile</CardTitle>
                    <Link to="/tutor/profile/edit">
                      <Button className="rounded-full bg-indigo-600 hover:bg-indigo-700">
                        Complete Full Profile
                      </Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Location</p>
                      <p className="font-medium">{profile.location || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Gender</p>
                      <p className="font-medium">{profile.gender || 'Not set'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Fee Range</p>
                      <p className="font-medium">₹{profile.fee_min || 0} - ₹{profile.fee_max || 0}/hr</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Languages</p>
                      <p className="font-medium">{profile.languages?.join(', ') || 'Not set'}</p>
                    </div>
                    <div className="md:col-span-2">
                      <p className="text-sm text-gray-500">Subjects</p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        {profile.subjects?.length > 0 ? (
                          profile.subjects.map((sub, idx) => (
                            <Badge key={idx} variant="secondary">{sub.subject || sub}</Badge>
                          ))
                        ) : (
                          <p className="text-gray-400">No subjects added</p>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {activeTab === 'reviews' && (
              <Card className="bg-white border-0 shadow-sm">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Star className="w-5 h-5 text-yellow-500" />
                    My Reviews ({reviews.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {reviews.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <Star className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                      <p>No reviews yet</p>
                      <p className="text-sm mt-2">Reviews from students will appear here</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {reviews.map((review) => (
                        <div key={review.id} className="p-4 border rounded-lg bg-gray-50">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold text-gray-900">{review.student_name}</p>
                              <div className="flex items-center gap-1 mt-1">
                                {[...Array(5)].map((_, i) => (
                                  <Star
                                    key={i}
                                    className={`w-4 h-4 ${
                                      i < review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
                                    }`}
                                  />
                                ))}
                                <span className="ml-2 text-sm text-gray-500">
                                  {review.rating}/5
                                </span>
                              </div>
                            </div>
                            <span className="text-sm text-gray-500">
                              {new Date(review.created_at).toLocaleDateString('en-IN', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <p className="text-gray-700 mt-3">{review.comment}</p>
                          {review.updated_at && (
                            <p className="text-xs text-gray-400 mt-2">
                              (Updated: {new Date(review.updated_at).toLocaleDateString()})
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Sidebar - Profile Card */}
          <div className="space-y-6">
            <Card className="bg-white border-0 shadow-sm overflow-hidden">
              <div className="h-20 bg-gradient-to-r from-indigo-500 to-purple-600" />
              <CardContent className="pt-0 relative">
                {/* Profile Photo */}
                <div className="relative -mt-12 mb-4 flex justify-center">
                  <div className="relative">
                    <Avatar className="w-24 h-24 border-4 border-white shadow-lg">
                      <AvatarImage src={profile.profile_photo} />
                      <AvatarFallback className="text-2xl bg-indigo-100 text-indigo-600">
                        {profile.name?.charAt(0) || 'T'}
                      </AvatarFallback>
                    </Avatar>
                    <button
                      onClick={() => {
                        setPhotoInputUrl(profile.profile_photo || '');
                        setShowPhotoModal(true);
                      }}
                      className="absolute bottom-0 right-0 w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white hover:bg-indigo-700 transition-colors shadow-lg"
                    >
                      <Camera className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Photo Upload Modal */}
                <Dialog open={showPhotoModal} onOpenChange={setShowPhotoModal}>
                  <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <Camera className="w-5 h-5 text-indigo-600" />
                        Upload Profile Photo
                      </DialogTitle>
                      <DialogDescription>
                        Upload a photo from your device (Max 100KB, JPG/PNG/WebP)
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      {/* Preview */}
                      <div className="flex justify-center">
                        <div className="relative">
                          <Avatar className="w-32 h-32 border-4 border-gray-200">
                            <AvatarImage src={photoInputUrl} />
                            <AvatarFallback className="text-3xl bg-gray-100 text-gray-400">
                              {profile.name?.charAt(0) || 'T'}
                            </AvatarFallback>
                          </Avatar>
                        </div>
                      </div>
                      
                      {/* File Upload */}
                      <div className="space-y-3">
                        <Label className="text-sm font-medium">Choose Photo</Label>
                        <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                          <input
                            type="file"
                            id="photo-file-input"
                            accept="image/jpeg,image/png,image/webp,image/jpg"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              // Check file size (30KB max)
                              if (file.size > 30 * 1024) {
                                toast.error('File too large! Maximum size is 30KB. Please compress your image.');
                                return;
                              }
                              
                              // Check file type
                              if (!['image/jpeg', 'image/png', 'image/webp', 'image/jpg'].includes(file.type)) {
                                toast.error('Invalid file type. Only JPG, PNG, and WebP are allowed.');
                                return;
                              }
                              
                              // Show preview
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setPhotoInputUrl(event.target.result);
                              };
                              reader.readAsDataURL(file);
                              
                              // Upload file
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              try {
                                toast.loading('Uploading photo...', { id: 'photo-upload' });
                                const response = await api.post('/tutor/profile/photo/upload', formData, {
                                  headers: { 'Content-Type': 'multipart/form-data' }
                                });
                                toast.success('Photo uploaded successfully!', { id: 'photo-upload' });
                                setShowPhotoModal(false);
                                loadProfile();
                              } catch (error) {
                                toast.error(error.response?.data?.detail || 'Failed to upload photo', { id: 'photo-upload' });
                              }
                            }}
                          />
                          <label htmlFor="photo-file-input" className="cursor-pointer">
                            <Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" />
                            <p className="text-sm font-medium text-gray-700">
                              Click to upload from device
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              JPG, PNG or WebP (Max 30KB)
                            </p>
                          </label>
                        </div>
                        
                        {/* Size Info */}
                        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
                          <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                          <span>Recommended: Square photo, at least 200x200 pixels</span>
                        </div>
                      </div>

                      {/* Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-gray-200" />
                        </div>
                        <div className="relative flex justify-center text-xs">
                          <span className="px-2 bg-white text-gray-500">OR enter URL</span>
                        </div>
                      </div>
                      
                      {/* URL Input (Alternative) */}
                      <div>
                        <div className="flex gap-2">
                          <div className="relative flex-1">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <Input
                              id="photo-url"
                              value={photoInputUrl.startsWith('data:') ? '' : photoInputUrl}
                              onChange={(e) => setPhotoInputUrl(e.target.value)}
                              placeholder="https://example.com/photo.jpg"
                              className="pl-10 text-sm"
                            />
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              if (!photoInputUrl.trim() || photoInputUrl.startsWith('data:')) {
                                toast.error('Please enter a valid URL');
                                return;
                              }
                              try {
                                await api.post(`/tutor/profile/photo?photo_url=${encodeURIComponent(photoInputUrl)}`);
                                toast.success('Photo updated!');
                                setShowPhotoModal(false);
                                loadProfile();
                              } catch (error) {
                                toast.error('Failed to update photo');
                              }
                            }}
                          >
                            Save URL
                          </Button>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex justify-end">
                      <Button
                        variant="outline"
                        onClick={() => setShowPhotoModal(false)}
                      >
                        Close
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                <div className="text-center mb-6">
                  <h2 className="text-xl font-bold text-gray-900">{profile.name}</h2>
                  <p className="text-sm text-gray-500">{profile.location || 'Location not set'}</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-4 h-4 ${
                          star <= Math.round(stats.rating || 0)
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-300'
                        }`}
                      />
                    ))}
                    <span className="text-sm text-gray-500 ml-1">({stats.reviews_count || 0})</span>
                  </div>
                </div>

                {/* Mini Stats */}
                <div className="grid grid-cols-3 gap-2 mb-6">
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Eye className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                    <p className="text-lg font-bold text-gray-900">{stats.profile_views || 0}</p>
                    <p className="text-xs text-gray-500">Views</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Send className="w-4 h-4 mx-auto text-gray-400 mb-1" />
                    <p className="text-lg font-bold text-gray-900">{stats.applications || 0}</p>
                    <p className="text-xs text-gray-500">Applications</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg">
                    <Coins className="w-4 h-4 mx-auto text-yellow-500 mb-1" />
                    <p className="text-lg font-bold text-yellow-600">{stats.coins || 0}</p>
                    <p className="text-xs text-gray-500">Coins</p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <Button 
                    className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-lg"
                    onClick={() => handleTabChange('messages')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    Messages
                    {unreadCount > 0 && (
                      <Badge className="ml-2 bg-red-500">{unreadCount}</Badge>
                    )}
                  </Button>
                  <Link to="/wallet" className="block">
                    <Button variant="outline" className="w-full rounded-lg">
                      <Wallet className="w-4 h-4 mr-2" />
                      Manage Wallet
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-lg"
                    onClick={() => handleTabChange('reviews')}
                  >
                    <Star className="w-4 h-4 mr-2" />
                    View Reviews
                    {reviewUnreadCount > 0 && (
                      <Badge className="ml-2 bg-red-500">{reviewUnreadCount}</Badge>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                    onClick={() => setShowDeleteModal(true)}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Profile
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Delete Profile Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 text-red-600">
                    <AlertTriangle className="w-5 h-5" />
                    Delete Profile
                  </DialogTitle>
                  <DialogDescription>
                    Are you sure you want to delete your profile? This action cannot be undone.
                    All your data including messages, reviews, and profile information will be permanently deleted.
                  </DialogDescription>
                </DialogHeader>
                <div className="flex gap-3 mt-4">
                  <Button 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={handleDeleteProfile}
                  >
                    Delete Permanently
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* Quick Tips */}
            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-0 shadow-sm">
              <CardContent className="p-4">
                <h3 className="font-semibold text-gray-900 mb-3">💡 Quick Tips</h3>
                <ul className="text-sm text-gray-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600">•</span>
                    Complete your profile to get more visibility
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600">•</span>
                    Add an intro video to increase trust
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-indigo-600">•</span>
                    Respond to messages quickly for better ratings
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={showNotificationsModal} onOpenChange={setShowNotificationsModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Notifications</DialogTitle>
            <DialogDescription>
              Latest activity for your account
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={notificationFilter === 'all' ? 'default' : 'outline'}
              onClick={() => setNotificationFilter('all')}
              className="rounded-full"
            >
              All
            </Button>
            <Button
              type="button"
              size="sm"
              variant={notificationFilter === 'message_received' ? 'default' : 'outline'}
              onClick={() => setNotificationFilter('message_received')}
              className="rounded-full"
            >
              Messages
            </Button>
            <Button
              type="button"
              size="sm"
              variant={notificationFilter === 'new_requirement_match' ? 'default' : 'outline'}
              onClick={() => setNotificationFilter('new_requirement_match')}
              className="rounded-full"
            >
              New Jobs
            </Button>
          </div>
          <div className="flex items-center justify-between text-xs text-gray-600">
            <span>
              New: <span className="font-semibold text-indigo-700">{unreadInFilter}</span> | Old: <span className="font-semibold">{filteredNotifications.length - unreadInFilter}</span>
            </span>
            {notificationsUnreadCount > 0 && (
              <button
                type="button"
                onClick={markAllNotificationsRead}
                className="text-indigo-600 hover:text-indigo-700"
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-y-auto space-y-3 pr-1">
            {filteredNotifications.length === 0 ? (
              <p className="text-sm text-gray-500">No notifications yet.</p>
            ) : filteredNotifications.map((n) => (
              <button
                type="button"
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`w-full text-left p-3 rounded-lg border transition-colors hover:border-indigo-300 ${
                  n.read ? 'bg-white' : 'bg-indigo-50 border-indigo-300'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-gray-900">{n.title}</p>
                  {!n.read && (
                    <span className="px-2 py-0.5 rounded-full bg-indigo-600 text-white text-[10px] font-semibold">
                      NEW
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-600">{n.body}</p>
                <p className="text-xs text-gray-400 mt-1">{formatDateIST(n.created_at)}</p>
                {n.read && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => {
                      e.stopPropagation();
                      markNotificationUnread(n.id);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        e.stopPropagation();
                        markNotificationUnread(n.id);
                      }
                    }}
                    className="inline-block mt-2 text-[11px] text-indigo-600 hover:text-indigo-700"
                  >
                    Mark as unread
                  </span>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
